import { AlertTriangle, BarChart3, Gift, Star, Store, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, Link } from 'react-router-dom';

import { LanguagePillSwitcher } from '../LanguagePillSwitcher';
import { Logo } from '../Logo';

interface AdminSidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const navLinkClassName = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
    isActive
      ? 'bg-sidebar-foreground/10 font-medium text-sidebar-foreground'
      : 'text-sidebar-foreground/70 hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground'
  }`;

function SidebarContent({ onNavigate }: { onNavigate: () => void }) {
  const { t } = useTranslation('admin');

  const navItems = [
    { to: '/admin', label: t('nav.stats'), icon: BarChart3, end: true },
    { to: '/admin/restaurants', label: t('nav.restaurants'), icon: Store, end: false },
    { to: '/admin/disputes', label: t('nav.disputes'), icon: AlertTriangle, end: false },
    { to: '/admin/reviews', label: t('nav.reviews'), icon: Star, end: false },
    { to: '/admin/vouchers', label: t('nav.vouchers'), icon: Gift, end: false },
  ];

  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex h-16 shrink-0 items-center border-b border-sidebar-foreground/10 px-4">
        <Link className="flex items-center gap-2" onClick={onNavigate} to="/">
          <Logo className="h-8 w-auto invert" variant="black" />
        </Link>
        <span className="ml-2 rounded-md bg-destructive/20 px-1.5 py-0.5 text-xs font-medium text-destructive-foreground">
          {t('nav.admin')}
        </span>
      </div>

      <nav className="flex-1 overflow-y-auto p-3">
        <ul className="space-y-0.5">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.to}>
                <NavLink
                  className={navLinkClassName}
                  end={item.end}
                  onClick={onNavigate}
                  to={item.to}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {item.label}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="shrink-0 border-t border-sidebar-foreground/10 p-3 space-y-0.5">
        <Link
          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-sidebar-foreground/70 hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground"
          onClick={onNavigate}
          to="/manage"
        >
          {t('nav.backoffice')}
        </Link>
        <Link
          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-sidebar-foreground/70 hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground"
          onClick={onNavigate}
          to="/"
        >
          {t('nav.customerSite')}
        </Link>
        <LanguagePillSwitcher className="mt-1" />
      </div>
    </div>
  );
}

export function AdminSidebar({ mobileOpen, onMobileClose }: AdminSidebarProps) {
  const { t } = useTranslation('admin');
  return (
    <>
      <aside className="hidden h-full w-64 shrink-0 flex-col border-r border-sidebar-foreground/10 bg-sidebar md:flex">
        <SidebarContent onNavigate={() => {}} />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-foreground/40 backdrop-blur-sm"
            onClick={onMobileClose}
          />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-sidebar shadow-xl">
            <button
              aria-label={t('common:close', 'Close menu')}
              className="absolute right-3 top-3 rounded-lg p-1.5 text-sidebar-foreground/70 hover:bg-sidebar-foreground/10"
              onClick={onMobileClose}
              type="button"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent onNavigate={onMobileClose} />
          </aside>
        </div>
      )}
    </>
  );
}
